import { useApolloClient, useMutation } from '@apollo/client/react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Field,
  HStack,
  Separator,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'

import { UserLoginForm } from '@src/components/sco/Account/UserLoginForm'
import { UserSignupRegisterForm } from '@src/components/sco/Account/UserSignupRegisterForm'
import {
  SessionBookExpert,
  sessionPriceUsd,
  useSessionBookExpert,
} from '@src/components/sco/Advisors/hooks/useSessionBookExpert'
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths'
import { SessionPrechargePayment } from '@src/components/sco/Advisors/sections/SessionPrechargePayment'
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState'
import { SessionDurationPicker } from '@src/components/sco/Advisors/shared/SessionDurationPicker'
import {
  SuggestedTimesFields,
  useSuggestedTimes,
} from '@src/components/sco/Advisors/shared/SuggestedTimesFields'
import { acceptingSessionsDirectoryHref } from '@src/components/sco/Advisors/utils/directoryFilters'
import {
  BookingEntry,
  BookingError,
  classifyBookingError,
  clearBookDraft,
  mergeDraftWithUrl,
  readBookDraft,
  REASON_MAX,
  resolveBookingSource,
  SessionBookDraft,
  validateBookDetails,
  writeBookDraft,
} from '@src/components/sco/Advisors/utils/sessionBookFlow'
import {
  formatHourlyRate,
  formatUsd,
  isSessionDurationMinutes,
  SESSION_DEFAULT_PACKAGE_MINUTES,
  timezoneAbbreviation,
} from '@src/components/sco/Advisors/utils/sessionFormat'
import { SESSION_STATUS } from '@src/components/sco/Advisors/utils/sessionStatus'
import {
  formatSessionWeeklyHoursPreference,
  parseSessionWeeklyHours,
} from '@src/components/sco/Advisors/utils/sessionWeeklyHours'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { PREPARE_ADVISOR_SESSION_PAYMENT } from '@src/gql/mutations/PrepareAdvisorSessionPayment'
import { REQUEST_ADVISOR_SESSION } from '@src/gql/mutations/RequestAdvisorSession'
import { ADVISOR_SESSIONS } from '@src/gql/queries/AdvisorSessions'
import {
  PrepareAdvisorSessionPaymentPayload,
  ScoAdvisorSessionCollection,
  ScoAdvisorSessionCharge,
  ScoAdvisorSession,
} from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { useRouter } from 'next/router'
import { FormEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { LuCreditCard, LuSend, LuUserRound } from 'react-icons/lu'

// ---------------------------------------------------------------------------
// Flow (presentational + state machine). Actions are injected so Storybook can
// drive every branch without Apollo, Stripe, or a signed-in user.
// ---------------------------------------------------------------------------

export type SessionBookPrepareInput = {
  expertUserId: string
  approximateLengthMinutes: number
  waiveCode: string | null
}

export type SessionBookRequestInput = SessionBookPrepareInput & {
  reason: string
  suggestedTimes: string[]
  paymentIntentId: string | null
  bookingSource: string | null
}

export type SessionBookActions = {
  prepare: (input: SessionBookPrepareInput) => Promise<PrepareAdvisorSessionPaymentPayload | null | undefined>
  /** Resolves to the new advisorSessionId. */
  request: (input: SessionBookRequestInput) => Promise<string | null | undefined>
  /** ALREADY_ACTIVE: where the seeker's open session with this expert lives. */
  findActiveSessionHref?: (expertUserId: string) => Promise<string | null>
}

/** A prepared hold. `minutes` / `waiveCode` must match between prepare and request. */
export type PreparedPayment = {
  paymentIntentId: string
  clientSecret: string
  amountUsd: number
  stripeConnectAccountId?: string | null
  customerSessionClientSecret?: string | null
  minutes: number
  waiveCode: string
}

export type SessionBookPaymentRenderProps = {
  payment: PreparedPayment
  onAuthorized: () => void | Promise<void>
  onCancel: () => void | Promise<void>
}

type Step =
  | { name: 'details' }
  | { name: 'auth' }
  | { name: 'payment'; payment: PreparedPayment }
  /** Hold is authorized but the request failed for a reason a retry may fix. */
  | { name: 'authorized'; payment: PreparedPayment }
  | { name: 'done'; advisorSessionId: string }

type FlowError = BookingError & { activeSessionHref?: string | null }

/** Drafts older than this don't auto-resume to payment after sign-in. */
const RESUME_WINDOW_MS = 60 * 60 * 1000

export type SessionBookFlowProps = {
  expertUserId: string
  expert: SessionBookExpert
  isGuest: boolean
  entry: BookingEntry
  /** `?waive=` from the URL (or the draft, when signup dropped it). */
  waiveCode?: string
  /** `?ref=` from the URL (or the draft). */
  refParam?: string
  initialDraft?: SessionBookDraft | null
  /** Persist the draft to sessionStorage (off in stories). */
  persistDraft?: boolean
  embedded?: boolean
  cancelHref: string
  sessionsHref: string
  changeAdvisorHref?: string
  actions: SessionBookActions
  onBooked: (advisorSessionId: string) => void
  /**
   * Register / log in. Signing in flips `isGuest` (the forms refetch the viewer),
   * which moves the flow on to prepare — the step needs no callback.
   */
  renderAuthStep?: () => ReactNode
  renderPayment?: (props: SessionBookPaymentRenderProps) => ReactNode
}

const defaultRenderPayment = ({ payment, onAuthorized, onCancel }: SessionBookPaymentRenderProps) => (
  <SessionPrechargePayment
    amountUsd={payment.amountUsd}
    cancelLabel={'Back'}
    clientSecret={payment.clientSecret}
    customerSessionClientSecret={payment.customerSessionClientSecret}
    stripeConnectAccountId={payment.stripeConnectAccountId}
    onAuthorized={onAuthorized}
    onCancel={onCancel}
  />
)

const StepLabel = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <HStack color={'colorPalette.fg/70'} gap={2}>
    {icon}
    <Text textStyle={'labelXs'}>{children}</Text>
  </HStack>
)

const FlowErrorBlock = ({
  error,
  sessionsHref,
  onDropWaive,
}: {
  error: FlowError
  sessionsHref: string
  onDropWaive?: () => void
}) => (
  <Alert.Root borderRadius={'md'} status={error.kind === 'already-active' ? 'info' : 'error'}>
    <Alert.Indicator />
    <VStack align={'stretch'} gap={2}>
      <Alert.Description fontFamily={'longform'}>{error.message}</Alert.Description>
      {error.kind === 'already-active' ? (
        <Button asChild alignSelf={'flex-start'} size={'sm'} variant={'outline'}>
          <NextLink href={error.activeSessionHref || sessionsHref}>
            {error.activeSessionHref ? 'Open your session' : 'View my sessions'}
          </NextLink>
        </Button>
      ) : null}
      {error.kind === 'already-used' ? (
        <Button asChild alignSelf={'flex-start'} size={'sm'} variant={'outline'}>
          <NextLink href={sessionsHref}>View my sessions</NextLink>
        </Button>
      ) : null}
      {error.kind === 'waive-invalid' && onDropWaive ? (
        <Button alignSelf={'flex-start'} size={'sm'} variant={'outline'} onClick={onDropWaive}>
          Book at the standard rate
        </Button>
      ) : null}
    </VStack>
  </Alert.Root>
)

/** The booking state machine: details → (guest: auth) → prepare → (paid: card) → request. */
export const SessionBookFlow = ({
  expertUserId,
  expert,
  isGuest,
  entry,
  waiveCode: initialWaiveCode = '',
  refParam = '',
  initialDraft = null,
  persistDraft = true,
  embedded = false,
  cancelHref,
  sessionsHref,
  changeAdvisorHref,
  actions,
  onBooked,
  renderAuthStep,
  renderPayment = defaultRenderPayment,
}: SessionBookFlowProps) => {
  const times = useSuggestedTimes(initialDraft?.times)
  const [reason, setReason] = useState(initialDraft?.reason || '')
  const [minutes, setMinutes] = useState(
    initialDraft && isSessionDurationMinutes(initialDraft.minutes)
      ? initialDraft.minutes
      : expert.defaultMinutes || SESSION_DEFAULT_PACKAGE_MINUTES
  )
  const [waiveCode, setWaiveCode] = useState(initialWaiveCode.trim())
  const [awaitingAuth, setAwaitingAuth] = useState(!!initialDraft?.awaitingAuth)
  const [step, setStep] = useState<Step>({ name: 'details' })
  const [busy, setBusy] = useState<null | 'preparing' | 'requesting'>(null)
  const [error, setError] = useState<FlowError | null>(null)
  const [validation, setValidation] = useState<string | null>(null)

  const complimentary = !!waiveCode
  const bookingSource = resolveBookingSource({ ref: refParam, waiveCode, entry })
  const hourlyRate = expert.hourlyRate
  const estimatedCost = isSessionDurationMinutes(minutes) ? sessionPriceUsd(expert, minutes) : null
  const displayCost = complimentary ? 0 : estimatedCost
  const paid = !!displayCost && displayCost > 0
  const preferredHours = formatSessionWeeklyHoursPreference(
    parseSessionWeeklyHours(expert.sessionWeeklyHours)
  )
  const expertTimezoneAbbr = expert.timezone ? timezoneAbbreviation(expert.timezone) : ''
  const preferenceNote = preferredHours
    ? `${expert.name} prefers ${preferredHours}${
        expertTimezoneAbbr ? ` (${expertTimezoneAbbr})` : ''
      }. Preference only — you can still suggest other times.`
    : null

  const timesKey = times.isoValues.join('|')

  // L12: the draft survives register/login and a refresh. Never holds payment data.
  useEffect(() => {
    if (!persistDraft || step.name === 'done') {
      return
    }
    writeBookDraft(expertUserId, {
      minutes,
      reason,
      times: times.isoValues,
      waiveCode,
      ref: refParam,
      awaitingAuth,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistDraft, expertUserId, minutes, reason, timesKey, waiveCode, refParam, awaitingAuth, step.name])

  const details = () => ({
    minutes,
    reason: reason.trim().slice(0, REASON_MAX),
    suggestedTimes: times.isoValues,
  })

  const fail = useCallback(
    async (err: unknown, phase: 'prepare' | 'request', payment: PreparedPayment | null) => {
      const classified = classifyBookingError(err, { hasWaiveCode: !!(payment?.waiveCode ?? waiveCode) })
      if (classified.kind === 'already-active') {
        const href = actions.findActiveSessionHref
          ? await actions.findActiveSessionHref(expertUserId).catch(() => null)
          : null
        setError({ ...classified, activeSessionHref: href })
        setStep({ name: 'details' })
        return 'stop' as const
      }
      setError(classified)
      if (classified.kind === 'card-not-authorized' && payment) {
        setStep({ name: 'payment', payment })
        return 'stop' as const
      }
      // Only a paid request re-prepares (a fresh hold); a free request never loops back here.
      if (classified.kind === 're-prepare' && phase === 'request' && payment) {
        return 're-prepare' as const
      }
      if (classified.kind === 'other' && phase === 'request' && payment) {
        setStep({ name: 'authorized', payment })
        return 'stop' as const
      }
      setStep({ name: 'details' })
      return 'stop' as const
    },
    [actions, expertUserId, waiveCode]
  )

  const runRequest = async (
    payment: PreparedPayment | null,
    prepared: { minutes: number; waiveCode: string }
  ) => {
    setBusy('requesting')
    const d = details()
    try {
      const advisorSessionId = await actions.request({
        expertUserId,
        reason: d.reason,
        suggestedTimes: d.suggestedTimes,
        approximateLengthMinutes: prepared.minutes,
        waiveCode: prepared.waiveCode || null,
        paymentIntentId: payment?.paymentIntentId || null,
        bookingSource,
      })
      if (!advisorSessionId) {
        setError({ kind: 'other', code: null, message: 'The session was not created. Please try again.' })
        setStep(payment ? { name: 'authorized', payment } : { name: 'details' })
        return
      }
      if (persistDraft) {
        clearBookDraft(expertUserId)
      }
      setError(null)
      setStep({ name: 'done', advisorSessionId })
      onBooked(advisorSessionId)
    } catch (err) {
      const next = await fail(err, 'request', payment)
      if (next === 're-prepare') {
        // The server released (or never had) a matching hold: start a fresh one, keep the message.
        await runPrepare({ keepError: true })
      }
    } finally {
      setBusy(null)
    }
  }

  const runPrepare = async ({ keepError = false }: { keepError?: boolean } = {}) => {
    if (!keepError) {
      setError(null)
    }
    setBusy('preparing')
    const snapshot = { minutes, waiveCode }
    try {
      const prepared = await actions.prepare({
        expertUserId,
        approximateLengthMinutes: snapshot.minutes,
        waiveCode: snapshot.waiveCode || null,
      })
      const preparedMinutes = Number(prepared?.approximateLengthMinutes) || snapshot.minutes
      if (!prepared?.paymentRequired) {
        // $0 or waived: no card step, no PaymentIntent.
        await runRequest(null, { minutes: preparedMinutes, waiveCode: snapshot.waiveCode })
        return
      }
      if (!prepared.clientSecret || !prepared.paymentIntentId) {
        setError({
          kind: 'other',
          code: null,
          message: 'Could not start the card step for this session. Payment may be unavailable right now.',
        })
        setStep({ name: 'details' })
        return
      }
      setStep({
        name: 'payment',
        payment: {
          paymentIntentId: prepared.paymentIntentId,
          clientSecret: prepared.clientSecret,
          amountUsd: Number(prepared.amountUsd ?? 0),
          stripeConnectAccountId: prepared.stripeConnectAccountId,
          customerSessionClientSecret: prepared.stripeCustomerSessionClientSecret,
          minutes: preparedMinutes,
          waiveCode: snapshot.waiveCode,
        },
      })
    } catch (err) {
      await fail(err, 'prepare', null)
    } finally {
      setBusy(prev => (prev === 'preparing' ? null : prev))
    }
  }

  const continueAfterAuth = () => {
    setAwaitingAuth(false)
    void runPrepare()
  }

  // Signed in on the auth step (inline form, or a redirect that refetched the viewer): continue.
  useEffect(() => {
    if (step.name === 'auth' && !isGuest) {
      continueAfterAuth()
    }
    // Only the sign-in transition should trigger a prepare, not every render's new closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, step.name])

  // Back from a social-login redirect with a fresh draft: resume where the guest left off.
  const resumedRef = useRef(false)
  useEffect(() => {
    if (resumedRef.current || isGuest || !initialDraft?.awaitingAuth) {
      return
    }
    resumedRef.current = true
    if (step.name !== 'details') {
      return
    }
    const savedAt = Date.parse(initialDraft.savedAt || '')
    const fresh = Number.isFinite(savedAt) && Date.now() - savedAt < RESUME_WINDOW_MS
    const problem = validateBookDetails(
      { minutes, reason, times: times.isoValues, acceptsSessions: expert.acceptsSessions },
      isSessionDurationMinutes
    )
    if (fresh && !problem) {
      continueAfterAuth()
    } else {
      setAwaitingAuth(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest])

  const onSubmitDetails = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    const problem = validateBookDetails(
      { minutes, reason, times: times.isoValues, acceptsSessions: expert.acceptsSessions },
      isSessionDurationMinutes
    )
    setValidation(problem)
    if (problem) {
      return
    }
    if (isGuest) {
      setAwaitingAuth(true)
      setStep({ name: 'auth' })
      return
    }
    void runPrepare()
  }

  const onPaymentAuthorized = async () => {
    if (step.name !== 'payment') {
      return
    }
    setError(null)
    await runRequest(step.payment, { minutes: step.payment.minutes, waiveCode: step.payment.waiveCode })
  }

  const backToDetails = () => {
    // The PaymentIntent was never authorized, so nothing is held and no session exists.
    setStep({ name: 'details' })
    setError(null)
  }

  const dropWaive = () => {
    setWaiveCode('')
    setError(null)
  }

  const summaryLine = `${minutes} min with ${expert.name}`

  if (step.name === 'done') {
    return (
      <Card.Root variant={'subtle'}>
        <Card.Body>
          <VStack align={'stretch'} gap={2}>
            <Text fontWeight={'semibold'}>Request sent</Text>
            <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
              {expert.name} will confirm one of your times. Opening your session…
            </Text>
          </VStack>
        </Card.Body>
      </Card.Root>
    )
  }

  if (step.name === 'auth') {
    return (
      <Card.Root variant={'subtle'}>
        <Card.Body>
          <VStack align={'stretch'} gap={4}>
            <StepLabel icon={<LuUserRound />}>Step 2 · Your account</StepLabel>
            <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
              Create a free account or log in to send your request. Your topic and times are saved
              {complimentary ? ', and the complimentary link still applies' : ''}.
            </Text>
            {renderAuthStep ? renderAuthStep() : null}
            <Button alignSelf={'flex-start'} size={'sm'} variant={'ghost'} onClick={backToDetails}>
              Back to details
            </Button>
          </VStack>
        </Card.Body>
      </Card.Root>
    )
  }

  if (step.name === 'payment' || step.name === 'authorized') {
    const payment = step.payment
    return (
      <Card.Root variant={'subtle'}>
        <Card.Body>
          <VStack align={'stretch'} gap={4}>
            <StepLabel icon={<LuCreditCard />}>{isGuest ? 'Step 3' : 'Step 2'} · Card hold</StepLabel>
            <HStack justify={'space-between'} wrap={'wrap'}>
              <Text fontWeight={'semibold'}>{summaryLine}</Text>
              <Text fontWeight={'semibold'}>{formatUsd(payment.amountUsd)}</Text>
            </HStack>
            {error ? <FlowErrorBlock error={error} sessionsHref={sessionsHref} /> : null}
            {step.name === 'authorized' ? (
              <VStack align={'stretch'} gap={3}>
                <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
                  Your card hold is in place, but the request did not go through. Retry to send it — you will
                  not be held twice. An unused hold is released automatically within 7 days.
                </Text>
                <HStack gap={3} wrap={'wrap'}>
                  <Button
                    loading={busy === 'requesting'}
                    variant={'cta'}
                    onClick={() =>
                      void runRequest(payment, { minutes: payment.minutes, waiveCode: payment.waiveCode })
                    }
                  >
                    <LuSend /> Retry booking
                  </Button>
                  <Button asChild variant={'outline'}>
                    <NextLink href={sessionsHref}>View my sessions</NextLink>
                  </Button>
                </HStack>
              </VStack>
            ) : (
              <Box aria-busy={busy === 'requesting'}>
                {renderPayment({ payment, onAuthorized: onPaymentAuthorized, onCancel: backToDetails })}
                {busy === 'requesting' ? (
                  <Text color={'colorPalette.fg/60'} fontSize={'sm'} mt={3}>
                    Card authorized. Sending your request…
                  </Text>
                ) : null}
              </Box>
            )}
          </VStack>
        </Card.Body>
      </Card.Root>
    )
  }

  const submitLabel = isGuest ? 'Continue' : paid ? 'Continue to payment' : 'Request session'

  return (
    <Card.Root variant={'subtle'}>
      <Card.Body>
        <form noValidate onSubmit={onSubmitDetails}>
          <VStack align={'stretch'} gap={5}>
            {!embedded ? (
              <HStack align={'start'} justify={'space-between'} wrap={'wrap'}>
                <VStack align={'stretch'} gap={2}>
                  <Text color={'colorPalette.fg/60'} textStyle={'labelXs'}>
                    Expert
                  </Text>
                  <HStack gap={2}>
                    <Avatar.Root size={'lg'}>
                      <Avatar.Image alt={expert.name} src={expert.photoUrl || undefined} />
                      <Avatar.Fallback>{expert.name.charAt(0).toUpperCase()}</Avatar.Fallback>
                    </Avatar.Root>
                    <VStack align={'stretch'} gap={0}>
                      <Text fontWeight={'semibold'}>{expert.name}</Text>
                      {formatHourlyRate(hourlyRate) ? (
                        <Text color={'colorPalette.fg/70'} fontSize={'sm'}>
                          {hourlyRate != null && hourlyRate > 0
                            ? `${formatHourlyRate(hourlyRate)} session`
                            : 'Free session'}
                        </Text>
                      ) : null}
                    </VStack>
                  </HStack>
                </VStack>
                {changeAdvisorHref ? (
                  <Button asChild size={'sm'} variant={'outline'}>
                    <NextLink href={changeAdvisorHref}>Change advisor</NextLink>
                  </Button>
                ) : null}
              </HStack>
            ) : (
              <VStack align={'stretch'} gap={1}>
                <Text fontWeight={'semibold'} textStyle={'lg'}>
                  {complimentary ? 'Complimentary session' : 'Book a session'}
                </Text>
                <Text color={'colorPalette.fg/60'} fontFamily={'longform'} textStyle={'sm'}>
                  {complimentary
                    ? 'This link makes the session free. Propose times that work for you.'
                    : 'Pick a package and propose times. We hold the package on your card before the request goes out.'}
                </Text>
              </VStack>
            )}

            <StepLabel icon={<LuSend />}>Step 1 · Package, topic and times</StepLabel>

            <SessionDurationPicker value={minutes} onChange={setMinutes} />

            {hourlyRate != null || displayCost != null ? (
              <Card.Root variant={'outline'}>
                <Card.Body py={3}>
                  <VStack align={'stretch'} gap={2}>
                    <HStack gap={3} justify={'space-between'} opacity={0.5} wrap={'wrap'}>
                      <Text color={'colorPalette.fg/60'} textStyle={'labelXs'}>
                        Cost
                      </Text>
                      <Text fontSize={'sm'} fontWeight={'semibold'}>
                        {complimentary ? 'Waived' : formatHourlyRate(hourlyRate) || '—'}
                      </Text>
                    </HStack>
                    <Separator />
                    <HStack gap={3} justify={'space-between'} wrap={'wrap'}>
                      <Text color={'colorPalette.fg'} fontWeight={'semibold'}>
                        {paid ? `${minutes} min package` : `Length · ${minutes} min`}
                      </Text>
                      <Text fontSize={'lg'} fontWeight={'semibold'}>
                        {paid ? formatUsd(displayCost) : 'Free'}
                      </Text>
                    </HStack>
                    <Text color={'colorPalette.fg/55'} fontFamily={'longform'} fontSize={'xs'}>
                      {complimentary
                        ? 'No card hold. The advisor still confirms one of your suggested times.'
                        : paid
                          ? 'We authorize this amount before your request is sent. You’re charged the full package when the advisor confirms a time. Cancel before anyone joins for a full refund.'
                          : 'No card hold for a free session. The expert still confirms one of your suggested times.'}
                    </Text>
                  </VStack>
                </Card.Body>
              </Card.Root>
            ) : null}

            <Field.Root required invalid={validation?.includes('topic')}>
              <Field.Label>Topic</Field.Label>
              <Textarea
                maxLength={REASON_MAX}
                placeholder={'Pricing, fundraising, GTM…'}
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value.slice(0, REASON_MAX))}
              />
              <Field.HelperText>
                {reason.length}/{REASON_MAX}
              </Field.HelperText>
            </Field.Root>

            <SuggestedTimesFields
              helperText={`${expert.name} picks one of these slots to confirm the session.`}
              label={'Suggested times'}
              preferenceNote={preferenceNote}
              times={times}
            />

            {validation ? (
              <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'} role={'alert'}>
                {validation}
              </Text>
            ) : null}
            {error ? (
              <FlowErrorBlock error={error} sessionsHref={sessionsHref} onDropWaive={dropWaive} />
            ) : null}
            {isGuest ? (
              <Text color={'colorPalette.fg/60'} fontFamily={'longform'} fontSize={'sm'}>
                Next, create a free account or log in{paid ? ', then authorize the card hold' : ''}.
              </Text>
            ) : null}
            <Separator marginY={2} width={'full'} />
            <HStack gap={3} wrap={'wrap'}>
              <Button loading={busy !== null} type={'submit'} variant={'cta'}>
                <LuSend /> {submitLabel}
              </Button>
              <Button asChild type={'button'} variant={'outline'}>
                <NextLink href={cancelHref}>Cancel</NextLink>
              </Button>
            </HStack>
          </VStack>
        </form>
      </Card.Body>
    </Card.Root>
  )
}

// ---------------------------------------------------------------------------
// Inline register / login step (L12). Signup keeps `?waive=` and `?ref=` because
// both forms return to the current URL.
// ---------------------------------------------------------------------------

export const SessionBookGuestAuth = () => {
  const [showLogin, setShowLogin] = useState(false)
  // Both forms refetch the viewer on success (no returnToHref → no redirect), which ends the guest state.
  return showLogin ? (
    <UserLoginForm title={'Log in to book'} onRegisterClick={() => setShowLogin(false)} />
  ) : (
    <UserSignupRegisterForm
      hideAlreadyLoggedInAlert
      title={'Create an account to book'}
      onLoginClick={() => setShowLogin(true)}
    />
  )
}

// ---------------------------------------------------------------------------
// Container: Apollo + router bindings.
// ---------------------------------------------------------------------------

type RequestSessionData = {
  requestAdvisorSession?: {
    advisorSession?: ScoAdvisorSession | null
    precharge?: ScoAdvisorSessionCharge | null
  } | null
}

const OPEN_STATUS_KEYS = [
  SESSION_STATUS.pending,
  SESSION_STATUS.verifyWithExpert,
  SESSION_STATUS.verifyWithSeeker,
  SESSION_STATUS.scheduleConfirmed,
  SESSION_STATUS.live,
]

export type SessionBookFormProps = {
  expertUserId: string
  waiveCode?: string
  /** `?ref=` (L18). */
  refParam?: string
  /** Where the form is rendered; drives the default bookingSource. */
  entry?: BookingEntry
  /** Hide expert picker + directory “change advisor”. */
  embedded?: boolean
  cancelHref?: string
}

export const SessionBookForm = ({
  expertUserId,
  waiveCode = '',
  refParam = '',
  entry = 'advisors',
  embedded = false,
  cancelHref,
}: SessionBookFormProps) => {
  const paths = useAdvisorsPaths()
  const router = useRouter()
  const client = useApolloClient()
  const { currentUser } = useCurrentUser()
  const isGuest = !currentUser || !!currentUser.isGuest
  const { expert, loading, error } = useSessionBookExpert(expertUserId)

  // sessionStorage is browser-only: read the draft after mount to keep SSR markup stable.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const [draft, setDraft] = useState<SessionBookDraft | null | undefined>(undefined)
  useEffect(() => {
    if (mounted && expertUserId) {
      setDraft(readBookDraft(expertUserId))
    }
  }, [mounted, expertUserId])

  const [prepareMutation] = useMutation<{
    prepareAdvisorSessionPayment?: PrepareAdvisorSessionPaymentPayload
  }>(PREPARE_ADVISOR_SESSION_PAYMENT)
  const [requestMutation] = useMutation<RequestSessionData>(REQUEST_ADVISOR_SESSION)

  const actions: SessionBookActions = {
    prepare: async input => {
      const result = await prepareMutation({ variables: { input } })
      return result.data?.prepareAdvisorSessionPayment
    },
    request: async input => {
      const result = await requestMutation({ variables: { input } })
      return result.data?.requestAdvisorSession?.advisorSession?.advisorSessionId
    },
    findActiveSessionHref: async id => {
      const result = await client.query<{ advisorSessions?: ScoAdvisorSessionCollection }>({
        query: ADVISOR_SESSIONS,
        variables: { role: 'SEEKER', statusKeys: OPEN_STATUS_KEYS, pageSize: 50, page: 1 },
        fetchPolicy: 'network-only',
      })
      const match = (result.data?.advisorSessions?.items || []).find(
        session => session?.expertUserId === id || session?.expertUser?.userId === id
      )
      return match?.advisorSessionId ? `${paths.sessions}/${match.advisorSessionId}` : null
    },
  }

  if (loading || !mounted || !router.isReady || draft === undefined) {
    return <AdvisorsQueryState loading>{null}</AdvisorsQueryState>
  }

  if (!expert || !expert.acceptsSessions) {
    return (
      <Card.Root variant={'subtle'}>
        <Card.Body>
          <VStack align={'stretch'} gap={3}>
            <Text color={'colorPalette.fg/70'} fontFamily={'longform'}>
              {!expert
                ? error
                  ? 'We could not load this advisor. Refresh to try again.'
                  : 'We could not find that advisor.'
                : `${expert.name} is not taking sessions right now.`}
            </Text>
            {!embedded ? (
              <Button asChild alignSelf={'flex-start'} variant={'cta'}>
                <NextLink href={acceptingSessionsDirectoryHref(paths)}>Choose another advisor</NextLink>
              </Button>
            ) : null}
          </VStack>
        </Card.Body>
      </Card.Root>
    )
  }

  const merged = mergeDraftWithUrl(draft, { waiveCode, ref: refParam })

  return (
    <SessionBookFlow
      key={expertUserId}
      actions={actions}
      cancelHref={cancelHref || paths.sessions}
      changeAdvisorHref={embedded ? undefined : acceptingSessionsDirectoryHref(paths)}
      embedded={embedded}
      entry={entry}
      expert={expert}
      expertUserId={expertUserId}
      initialDraft={draft}
      isGuest={isGuest}
      refParam={merged.ref}
      renderAuthStep={() => <SessionBookGuestAuth />}
      sessionsHref={paths.sessions}
      waiveCode={merged.waiveCode}
      onBooked={id => void router.push(`${paths.sessions}/${id}`)}
    />
  )
}
